import { generateId, getPhaseColor } from '../utils/helpers';
import { DataHubAPI } from '../utils/dataHub';
import { Feature, FeatureFormData, Initiative } from '../types/index';
import { WORK_PRODUCT_LIBRARY } from '../constants/index';

export const createFeatureHandlers = (
  initiatives: Initiative[],
  selectedProjectId: string | null,
  selectedFeature: Feature | null,
  setInitiatives: React.Dispatch<React.SetStateAction<Initiative[]>>,
  setSelectedFeature: React.Dispatch<React.SetStateAction<Feature | null>>,
  setLastUsedMatrixId: React.Dispatch<React.SetStateAction<string | null>>,
  setFeatureCreationProgress: React.Dispatch<React.SetStateAction<any>>,
  setShowAddFeatureModal: React.Dispatch<React.SetStateAction<boolean>>,
  setFeatureInfoData: React.Dispatch<React.SetStateAction<FeatureFormData>>,
  setShowEditWarning: React.Dispatch<React.SetStateAction<boolean>>,
  setIsFeatureInfoEditing: React.Dispatch<React.SetStateAction<boolean>>,
  setImpactedWorkProducts: React.Dispatch<React.SetStateAction<string[]>>,
  setShowWorkProductsImpactModal: React.Dispatch<React.SetStateAction<boolean>>,
  getFeatureMockData: (name: string) => any
) => {
  const handleCreateFeature = (featureData: FeatureFormData) => {
    setFeatureCreationProgress({
      status: 'loading',
      step: 'Creating feature structure...',
      currentStep: 1,
      totalSteps: 3,
      duration: 6000
    });
    
    setTimeout(() => {
      setFeatureCreationProgress(prev => ({
        ...prev,
        step: 'Setting up SDLC phases...',
        currentStep: 2
      }));
      
      setTimeout(() => {
        setFeatureCreationProgress(prev => ({
          ...prev,
          step: 'Generating requirements...',
          currentStep: 3
        }));
        
        setTimeout(() => {
          const currentProject = initiatives.find(init => init.id === selectedProjectId);
          if (currentProject) {
            const newFeature: Feature = {
              id: generateId(),
              name: featureData.title,
              sdlcPhases: Object.keys(WORK_PRODUCT_LIBRARY).map(phaseId => ({
                id: phaseId,
                name: phaseId === 'qa' ? 'QA' : phaseId.charAt(0).toUpperCase() + phaseId.slice(1),
                color: getPhaseColor(phaseId),
                workProducts: []
              })),
              detailedRequirements: [
                ...featureData.goals.map((goal: string) => ({
                  id: generateId(),
                  name: `Goal: ${goal}`,
                  description: `Feature goal: ${goal}`,
                  status: 'available',
                  approved: false
                })),
                ...featureData.successMetrics.map((metric: string) => ({
                  id: generateId(),
                  name: `Success Metric: ${metric}`,
                  description: `Success metric to track: ${metric}`,
                  status: 'available',
                  approved: false
                }))
              ],
              uploads: []
            };

            setInitiatives(prev => 
              prev.map(init => 
                init.id === selectedProjectId 
                  ? { ...init, features: [...init.features, newFeature] }
                  : init
              )
            );

            setSelectedFeature(newFeature);
            setLastUsedMatrixId(newFeature.id);

            // Create data change event for governance tracking
            const changeEvent: any = {
              id: generateId(),
              entityType: 'feature',
              entityId: newFeature.id,
              changeType: 'create',
              fieldChanges: {
                name: { from: null, to: newFeature.name },
                goals: { from: [], to: featureData.goals },
                successMetrics: { from: [], to: featureData.successMetrics }
              },
              timestamp: new Date(),
              propagationTargets: [selectedProjectId]
            };

            DataHubAPI.propagateChange(changeEvent);
            
            setFeatureCreationProgress({
              status: 'success',
              message: `Feature "${featureData.title}" created successfully with governance compliance`
            });
            
            setTimeout(() => {
              setFeatureCreationProgress(null);
            }, 3000);
          }
          
          setShowAddFeatureModal(false);
        }, 2000);
      }, 2000);
    }, 2000);
  };

  const handleFeatureInfoEdit = () => {
    if (selectedFeature) {
      const featureData = getFeatureMockData(selectedFeature.name);
      setFeatureInfoData({
        title: selectedFeature.name,
        description: featureData.description,
        goals: featureData.goals,
        successMetrics: featureData.metrics
      });
      setShowEditWarning(true);
    }
  };

  const handleFeatureInfoSave = () => {
    const mockAffectedWorkProducts = [
      'User Story Templates',
      'Acceptance Criteria',
      'Technical Specifications',
      'API Documentation',
      'UI/UX Mockups'
    ];
    
    setImpactedWorkProducts(mockAffectedWorkProducts);
    setShowWorkProductsImpactModal(true);
  };

  const handleGenerateWithAI = () => {
    if (selectedFeature) {
      const aiGeneratedDescription = `An advanced ${selectedFeature.name.toLowerCase()} system designed to enhance user experience through intelligent automation, real-time data processing, and seamless integration with existing workflows. This feature leverages cutting-edge technology to deliver scalable, secure, and user-friendly functionality that drives measurable business value.`;
      
      setFeatureInfoData(prev => ({
        ...prev,
        description: aiGeneratedDescription
      }));
    }
  };

  return {
    handleCreateFeature,
    handleFeatureInfoEdit,
    handleFeatureInfoSave,
    handleGenerateWithAI
  };
};